/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { CalendarIcon, GlobeIcon, IdCardIcon, Link2Icon } from '@/shared/ui';
import { TagIcon } from '@/shared/ui/components/basic/icons';
import { SectionCard } from '@/features/property-management/components/PropertyDetailView/SectionCard';
import { formatLocalizedDate } from '@/shared/utils/date';
import type { ClientRow } from '../../context/ClientManagementContext';
import { ContactRow } from './helpers';

interface OverviewOriginSectionProps {
  client: ClientRow;
}

/**
 * Turns a raw lead-origin token into a display label, falling back to the
 * token itself so channels the catalog does not know about still read as
 * something ("tiktok" -> "Tiktok") instead of disappearing.
 *
 * @param t - The clients-namespace translation function.
 * @param group - The i18n subgroup (`platform` or `source`).
 * @param value - The raw token stored on the lead origin.
 * @returns The localized label, or an empty string when there is no token.
 */
function originLabel(t: TFunction, group: 'platform' | 'source', value?: string): string {
  if (!value) return '';
  const humanized = value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
  return t(`leadOrigin.${group}.${value}`, { defaultValue: humanized });
}

/**
 * Origin SectionCard for the client overview: which channel the client came
 * in through, what kind of interaction captured them, when, the campaign,
 * and the client's code in the previous system.
 *
 * Always rendered — a client with no tracked origin reads as a manual
 * registration rather than silently dropping the section.
 *
 * @param props - The client row whose lead origin is rendered.
 * @returns The rendered origin section card.
 */
export function OverviewOriginSection({ client }: OverviewOriginSectionProps) {
  const { t } = useTranslation('features/clients');
  const origin = client.lead_origin;

  // `lead_origin.platform` is marketing/chat-capture attribution and is
  // absent for anything that didn't come through a platform webhook --
  // `origin_channel` (how the record itself was created: manual, a
  // spreadsheet import...) is what actually answers "cadastro manual?"
  // when the platform-specific one has nothing to say.
  const channel = originLabel(t, 'platform', origin?.platform)
    || t(`profile.origin.channels.${client.origin_channel}`, {
      defaultValue: t('profile.origin.manual'),
    });
  const source = originLabel(t, 'source', origin?.source);
  const capturedAt = origin?.first_captured_at
    ? formatLocalizedDate(origin.first_captured_at)
    : '';

  return (
    <SectionCard data-testid="client-origin" title={t('profile.origin.title')}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[var(--space-3)]">
        <ContactRow
          icon={<GlobeIcon width={16} height={16} />}
          label={t('profile.origin.channel')}
          value={channel}
        />
        {client.origin_detail ? (
          <ContactRow
            icon={<TagIcon width={16} height={16} />}
            label={t('profile.origin.arrived')}
            value={client.origin_detail}
          />
        ) : null}
        {source ? (
          <ContactRow
            icon={<Link2Icon width={16} height={16} />}
            label={t('profile.origin.source')}
            value={source}
          />
        ) : null}
        {capturedAt ? (
          <ContactRow
            icon={<CalendarIcon width={16} height={16} />}
            label={t('profile.origin.capturedAt')}
            value={capturedAt}
          />
        ) : null}
        {client.origin_campaign ? (
          <ContactRow
            icon={<TagIcon width={16} height={16} />}
            label={t('profile.origin.campaign')}
            value={client.origin_campaign}
          />
        ) : null}
        {client.legacy_code ? (
          <ContactRow
            icon={<IdCardIcon width={16} height={16} />}
            label={t('profile.origin.legacyCode')}
            value={client.legacy_code}
          />
        ) : null}
      </div>
    </SectionCard>
  );
}
